import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const RevenueAnalytics = ({ metrics }) => {
  const getMetricIcon = (type) => {
    switch (type) {
      case 'collection':
        return 'TrendingUp';
      case 'ar':
        return 'Clock';
      case 'denial':
        return 'XCircle';
      case 'revenue':
        return 'DollarSign';
      default:
        return 'BarChart3';
    }
  };

  const getMetricColor = (type) => {
    switch (type) {
      case 'collection':
        return 'bg-success/10 text-success';
      case 'ar':
        return 'bg-warning/10 text-warning';
      case 'denial':
        return 'bg-destructive/10 text-destructive';
      case 'revenue':
        return 'bg-primary/10 text-primary';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return 'TrendingUp';
    if (trend < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getTrendColor = (trend, isPositiveGood = true) => {
    if (trend === 0) return 'text-muted-foreground';
    if (isPositiveGood) {
      return trend > 0 ? 'text-success' : 'text-destructive';
    }
    return trend > 0 ? 'text-destructive' : 'text-success';
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground">Revenue Analytics</h2>
        <Button variant="ghost" size="sm" iconName="Download" iconPosition="left">
          <span className="hidden sm:inline">Export</span>
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {metrics?.map((metric) => (
          <div key={metric?.id} className="bg-muted/50 rounded-lg p-4 border border-border transition-smooth hover:shadow-elevation-2">
            <div className="flex items-start justify-between mb-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${getMetricColor(metric?.type)}`}>
                <Icon name={getMetricIcon(metric?.type)} size={20} />
              </div>
              {metric?.trend !== undefined && (
                <div className={`flex items-center gap-1 ${getTrendColor(metric?.trend, metric?.isPositiveGood)}`}>
                  <Icon name={getTrendIcon(metric?.trend)} size={16} />
                  <span className="text-xs font-semibold">{Math.abs(metric?.trend)}%</span>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">{metric?.label}</p>
              <p className="text-xl md:text-2xl font-bold text-foreground mb-1">{metric?.value}</p>
              {metric?.subtitle && (
                <p className="text-xs text-muted-foreground">{metric?.subtitle}</p>
              )}
            </div>

            {metric?.benchmark && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Benchmark</span>
                  <span className="font-medium text-foreground">{metric?.benchmark}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-4">Performance Trends</h3>
        <div className="space-y-3">
          {metrics?.slice(0, 3)?.map((metric) => (
            <div key={`trend-${metric?.id}`} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{metric?.label}</span>
                  <span className="text-sm font-semibold text-foreground">{metric?.value}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      metric?.type === 'collection' ? 'bg-success' :
                      metric?.type === 'ar' ? 'bg-warning' :
                      metric?.type === 'denial'? 'bg-destructive' : 'bg-primary'
                    }`}
                    style={{ width: `${metric?.percentage || 75}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalytics;